/**
 * Create Fleet Modal Component
 *
 * Modal wizard for creating new OTA fleets with AWS IoT Thing Group integration.
 */

import React, { memo, useState } from "react";
import { toast } from "react-toastify";
import { otaLogger } from "../../logger";

const log = otaLogger.byPrefix("CreateFleetModal");
import styled from "styled-components";
import API from "Utils/api";

interface CreateFleetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateFleetModal = memo<CreateFleetModalProps>(
  ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [fleetName, setFleetName] = useState("");
    const [description, setDescription] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // Validation
      if (!fleetName.trim()) {
        toast.error("Fleet name is required");
        return;
      }

      if (fleetName.length < 3 || fleetName.length > 50) {
        toast.error("Fleet name must be 3-50 characters");
        return;
      }

      try {
        setLoading(true);

        const response = await API.post("/ota/fleets/", {
          name: fleetName.trim(),
          description: description.trim(),
        });

        toast.success(
          `Fleet "${response.data.name}" created successfully. AWS Thing Group: ${response.data.aws_thing_group_name}`
        );

        // Reset form
        setFleetName("");
        setDescription("");

        onSuccess();
        onClose();
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.name?.[0] ||
          err.response?.data?.error ||
          "Failed to create fleet";
        toast.error(errorMsg);
        log.error("Fleet creation failed", err as Record<string, any>);
      } finally {
        setLoading(false);
      }
    };

    if (!isOpen) return null;

    return (
      <ModalBackdrop onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Create New Fleet</ModalTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>

          <ModalBody>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="fleetName">
                  Fleet Name <Required>*</Required>
                </Label>
                <Input
                  id="fleetName"
                  type="text"
                  value={fleetName}
                  onChange={(e) => setFleetName(e.target.value)}
                  placeholder="e.g., Production Fleet West, Test Fleet..."
                  disabled={loading}
                  maxLength={50}
                />
                <HelpText>
                  Unique fleet name (3-50 characters). AWS Thing Group will be
                  auto-generated.
                </HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description (Optional)</Label>
                <TextArea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter fleet description/purpose..."
                  disabled={loading}
                  rows={3}
                  maxLength={500}
                />
                <HelpText>Optional description (max 500 characters)</HelpText>
              </FormGroup>

              <InfoBox>
                <strong>What happens next:</strong>
                <ul>
                  <li>Fleet record created in database</li>
                  <li>AWS IoT Thing Group provisioned automatically</li>
                  <li>Ready for vehicle assignments</li>
                </ul>
              </InfoBox>

              <ButtonGroup>
                <CancelButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? "Creating Fleet..." : "Create Fleet"}
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalBody>
        </ModalContainer>
      </ModalBackdrop>
    );
  }
);

CreateFleetModal.displayName = "CreateFleetModal";

// Styled Components
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Required = styled.span`
  color: #e53e3e;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #666;
`;

const InfoBox = styled.div`
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  padding: 12px;
  margin: 20px 0;
  font-size: 13px;

  strong {
    display: block;
    margin-bottom: 8px;
    color: #0050b3;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: white;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  background: #48bb78;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #38a169;
  }

  &:disabled {
    background: #9ae6b4;
    cursor: not-allowed;
  }
`;
